import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  TrendingUp, DollarSign, ShoppingCart, Users, Store,
  Package, ArrowUpRight, ArrowDownRight, Calendar, Star,
  RefreshCw, ShoppingBag, RotateCcw, Percent, Eye, X, ImageOff, Search,
} from 'lucide-react'
import { imgUrl } from '@/utils/img'
import { analyticsService } from '@/services/analyticsService'
import { usersService } from '@/services/usersService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import Pagination from '@/components/shared/Pagination'
import DateRangePicker from '@/components/shared/DateRangePicker'
import { usePagination } from '@/hooks/usePagination'
import { formatCurrency, formatNumber, formatRelativeTime, getLocalizedField } from '@/utils/formatters'
import { ROUTES } from '@/constants/routes'

// ─── Cart Detail Modal ────────────────────────────────────────────────────────
function CartDetailModal({ user, onClose }) {
  const { data: cart, isLoading } = useQuery({
    queryKey: ['user-cart', user?.id],
    queryFn: () => usersService.getCart(user.id),
    enabled: !!user,
  })

  if (!user) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(26,28,28,0.4)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 24, width: '100%', maxWidth: 560,
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 24px 64px rgba(26,28,28,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #F3F3F3', position: 'sticky', top: 0,
          background: '#fff', borderRadius: '24px 24px 0 0', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: 'rgba(255,109,0,0.08)', color: '#FF6D00',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShoppingCart size={17} />
            </div>
            <div>
              <p style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 15, color: '#1A1C1C', margin: 0 }}>
                سلة {user.name}
              </p>
              <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', margin: 0 }}>
                {user.email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: 10, border: 'none',
              background: '#F3F3F3', color: '#767683',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <LoadingSpinner />
            </div>
          ) : !cart || cart.items?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
              السلة فارغة
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {cart.items.map((item) => {
                  const image = item.variant?.imageUrl || item.product?.images?.[0]?.url
                  const price = Number(item.priceSnapshot)
                  const isUnavailable = item.product?.status !== 'active' || (item.variant && !item.variant.isActive)

                  return (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '12px 14px', borderRadius: 16,
                      border: `1px solid ${isUnavailable ? 'rgba(186,26,26,0.15)' : '#F0F0F0'}`,
                      background: isUnavailable ? 'rgba(186,26,26,0.02)' : '#FAFAFA',
                    }}>
                      {/* Image */}
                      <div style={{
                        width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                        background: '#E2E2E2', overflow: 'hidden',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {image
                          ? <img src={image} alt={getLocalizedField(item.product?.name, 'ar')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <ImageOff size={18} style={{ color: '#C4C4C4' }} />
                        }
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getLocalizedField(item.product?.name, 'ar')}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                          {item.variant && (
                            <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(0,6,102,0.06)', color: '#000666' }}>
                              {getLocalizedField(item.variant.name, 'ar')}
                            </span>
                          )}
                          <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683' }}>
                            {getLocalizedField(item.product?.vendor?.storeName, 'ar')}
                          </span>
                          {isUnavailable && (
                            <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(186,26,26,0.08)', color: '#BA1A1A' }}>
                              غير متاح
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Qty × price */}
                      <div style={{ textAlign: 'left', flexShrink: 0 }}>
                        <p style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#FF6D00', margin: 0 }}>
                          {formatCurrency(price * item.quantity)}
                        </p>
                        <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', margin: '2px 0 0' }}>
                          {item.quantity} × {formatCurrency(price)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Total */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginTop: 16, padding: '12px 16px', borderRadius: 14,
                background: 'rgba(255,109,0,0.05)', border: '1px solid rgba(255,109,0,0.12)',
              }}>
                <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#454652' }}>
                  الإجمالي ({cart.itemCount} منتج)
                </span>
                <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 16, color: '#FF6D00' }}>
                  {formatCurrency(cart.subtotal)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Date range presets ────────────────────────────────────────────────────────
const RANGES = [
  { key: '7d',  label: 'آخر 7 أيام', days: 7  },
  { key: '30d', label: 'آخر 30 يوم', days: 30 },
  { key: '90d', label: 'آخر 3 أشهر', days: 90 },
  { key: 'custom', label: 'مخصص', days: null },
]

function getDateRange(days) {
  const to   = new Date()
  const from = new Date()
  from.setDate(from.getDate() - days)
  return {
    from: from.toISOString().split('T')[0],
    to:   to.toISOString().split('T')[0],
  }
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, isCurrency }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: '#fff', border: '1px solid #E8E8E8',
      borderRadius: 12, padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
      fontFamily: 'Tajawal, sans-serif', direction: 'rtl',
    }}>
      <p style={{ color: '#767683', fontSize: 11, marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 700, fontSize: 13 }}>
          {isCurrency ? formatCurrency(p.value) : formatNumber(p.value)}
        </p>
      ))}
    </div>
  )
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, growth, color, isCurrency, isPercent, invertGrowth }) {
  const isPositive = invertGrowth ? growth <= 0 : growth >= 0
  return (
    <div className="hg-card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: `${color}12`, color }}>
          <Icon size={22} />
        </div>
        {growth != null && (
          <div className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full"
            style={{
              background: isPositive ? 'rgba(27,109,36,0.1)' : 'rgba(186,26,26,0.1)',
              color: isPositive ? '#1B6D24' : '#BA1A1A',
            }}>
            {isPositive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(growth).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="font-black text-2xl text-navy mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>
        {isCurrency ? formatCurrency(value) : isPercent ? `${(value ?? 0).toFixed(1)}%` : formatNumber(value ?? 0)}
      </div>
      <div className="text-sm text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</div>
      {sub && <div className="text-xs mt-1" style={{ color, fontFamily: 'Tajawal, sans-serif' }}>{sub}</div>}
    </div>
  )
}

// ── Status Pill ───────────────────────────────────────────────────────────────
function StatusPill({ label, total, pending, color, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="hg-card p-4 flex items-center gap-3 w-full text-right transition-all hover:-translate-y-0.5"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}12`, color }}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-xl" style={{ color, fontFamily: 'Cairo, sans-serif' }}>
          {formatNumber(total ?? 0)}
        </div>
        <div className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</div>
      </div>
      {pending > 0 && (
        <div className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ background: 'rgba(255,109,0,0.1)', color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}>
          {pending} معلق
        </div>
      )}
    </button>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children, action }) {
  return (
    <div className="hg-card p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-black text-navy text-base" style={{ fontFamily: 'Cairo, sans-serif' }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [range, setRange] = useState('30d')
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [customRange, setCustomRange] = useState(null)
  const [cartModalUser, setCartModalUser] = useState(null)
  const navigate = useNavigate()
  const rangeConfig = RANGES.find(r => r.key === range)
  const dateRange = range === 'custom' && customRange
    ? customRange
    : getDateRange(rangeConfig.days)
  const { page: cartsPage, limit: cartsLimit, setPage: setCartsPage } = usePagination(1, 10)

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: overview, isLoading: ovLoading } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: analyticsService.getOverview,
    retry: 1,
  })

  const { data: revenueData, isLoading: revLoading } = useQuery({
    queryKey: ['analytics-revenue', range],
    queryFn: () => analyticsService.getRevenue(dateRange),
    retry: 1,
  })

  const { data: topProducts } = useQuery({
    queryKey: ['analytics-top-products'],
    queryFn: () => analyticsService.getTopProducts(),
    retry: 1,
  })

  const { data: topVendors } = useQuery({
    queryKey: ['analytics-top-vendors'],
    queryFn: () => analyticsService.getTopVendors(),
    retry: 1,
  })

  const { data: topSearches } = useQuery({
    queryKey: ['analytics-top-searches', range],
    queryFn: () => analyticsService.getTopSearches({ ...dateRange, limit: 15 }),
    retry: 1,
  })

  const { data: usersGrowth } = useQuery({
    queryKey: ['analytics-users-growth', range],
    queryFn: () => analyticsService.getUsersGrowth(dateRange),
    retry: 1,
  })

  const { data: activeCarts } = useQuery({
    queryKey: ['analytics-active-carts', cartsPage, cartsLimit],
    queryFn: () => analyticsService.getActiveCarts({ page: cartsPage, limit: cartsLimit }),
    retry: 1,
  })

  const revenue  = revenueData   || []
  const products = topProducts   || []
  const vendors  = topVendors    || []
  const growth   = usersGrowth   || []
  const searches = topSearches   || []
  const carts    = activeCarts?.data || []
  const cartsMeta = activeCarts?.meta || {}

  const ov = overview || {}

  return (
    <div className="space-y-6">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-black text-navy text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>
            التحليلات والتقارير
          </h1>
          <p className="text-sm text-ghost mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            نظرة شاملة على أداء المنصة
          </p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => r.key === 'custom' ? setShowDatePicker(true) : setRange(r.key)}
              className="px-4 py-2 rounded-xl text-sm font-bold transition-all"
              style={{
                background: range === r.key ? '#000666' : 'rgba(0,6,102,0.06)',
                color:      range === r.key ? '#fff'    : '#000666',
                fontFamily: 'Tajawal, sans-serif',
              }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI Row 1 — Revenue & Orders ──────────────────────────────────── */}
      {ovLoading ? (
        <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard
              icon={DollarSign}
              label="إجمالي GMV (قيمة المبيعات)"
              value={ov.totalRevenue ?? 0}
              color="#1B6D24"
              isCurrency
            />
            <KpiCard
              icon={TrendingUp}
              label="دخل المنصة (العمولات)"
              value={ov.totalPlatformIncome ?? 0}
              sub={`${((ov.totalPlatformIncome / (ov.totalRevenue || 1)) * 100).toFixed(1)}% من GMV`}
              color="#1B6D24"
              isCurrency
            />
            <KpiCard
              icon={ShoppingCart}
              label="إجمالي الطلبات"
              value={ov.totalOrders ?? 0}
              sub={`${formatNumber(ov.completedOrders ?? 0)} مكتمل · ${formatNumber(ov.pendingOrders ?? 0)} معلق`}
              color="#000666"
            />
            <KpiCard
              icon={Users}
              label="إجمالي المستخدمين"
              value={ov.totalUsers ?? 0}
              sub={`${formatNumber(ov.activeCarts ?? 0)} عربة نشطة`}
              color="#FF6D00"
            />
          </div>

          {/* ── KPI Row 2 — Performance Metrics ───────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard
              icon={ShoppingBag}
              label="متوسط قيمة الطلب"
              value={ov.averageOrderValue ?? 0}
              color="#000666"
              isCurrency
            />
            <KpiCard
              icon={Percent}
              label="نسبة التحويل"
              value={ov.conversionRate ?? 0}
              sub="مكتملة / (مكتملة + عربات)"
              color="#1B6D24"
              isPercent
            />
            <KpiCard
              icon={RotateCcw}
              label="معدل الإرجاع"
              value={ov.refundRate ?? 0}
              sub={`${formatNumber(ov.totalReturns ?? 0)} مرتجع إجمالي`}
              color={ov.refundRate > 5 ? '#BA1A1A' : '#767683'}
              isPercent
              invertGrowth
            />
            <KpiCard
              icon={Store}
              label="البائعون المعتمدون"
              value={ov.totalVendors ?? 0}
              color="#000666"
            />
          </div>

          {/* ── Status Pills — Pending Items ───────────────────────────────── */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <StatusPill
              icon={Package}
              label="المنتجات"
              total={ov.totalProducts}
              pending={ov.pendingProducts}
              color="#000666"
              onClick={() => navigate(ROUTES.PRODUCTS)}
            />
            <StatusPill
              icon={Star}
              label="التقييمات"
              total={ov.totalReviews}
              pending={ov.pendingReviews}
              color="#FF6D00"
              onClick={() => navigate(ROUTES.REVIEWS)}
            />
            <StatusPill
              icon={RotateCcw}
              label="المرتجعات"
              total={ov.totalReturns}
              pending={ov.pendingReturns}
              color="#BA1A1A"
              onClick={() => navigate(ROUTES.RETURNS)}
            />
            <StatusPill
              icon={ShoppingCart}
              label="عربات التسوق النشطة"
              total={ov.activeCarts}
              pending={0}
              color="#1B6D24"
            />
          </div>
        </>
      )}

      {/* ── Revenue Chart ─────────────────────────────────────────────────── */}
      <Section
        title="الإيرادات عبر الزمن"
        action={
          <div className="flex items-center gap-1.5 text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <Calendar size={13} />
            {dateRange.from} — {dateRange.to}
          </div>
        }
      >
        {revLoading ? (
          <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
        ) : revenue.length === 0 ? (
          <div className="text-center py-16 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            لا توجد بيانات للفترة المحددة
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenue} margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#1B6D24" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#1B6D24" stopOpacity={0}    />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F3F3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'Tajawal, sans-serif', fill: '#767683' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: 'Tajawal, sans-serif', fill: '#767683' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip isCurrency />} />
              <Area type="monotone" dataKey="revenue" stroke="#1B6D24" strokeWidth={2.5} fill="url(#revGrad)" dot={false} activeDot={{ r: 5, fill: '#1B6D24' }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* ── Top Products & Top Vendors ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        <Section title="أكثر المنتجات مبيعاً">
          {products.length === 0 ? (
            <div className="text-center py-8 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>لا توجد بيانات</div>
          ) : (
            <div className="space-y-3">
              {products.slice(0, 8).map((item, idx) => {
                const prod    = item.product || item
                const name    = prod.name?.ar || prod.name?.en || '—'
                const vendor  = prod.vendor?.storeName?.ar || prod.vendor?.storeName?.en || ''
                const imgSrc  = prod.images?.[0]?.url
                const orders  = item.totalOrders ?? 0
                const rev     = item.totalRevenue ?? 0
                return (
                  <div key={prod.id || idx} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                      style={{ background: idx < 3 ? '#FF6D00' : '#F3F3F3', color: idx < 3 ? '#fff' : '#767683' }}>
                      {idx + 1}
                    </span>
                    <div style={{ width:36, height:36, borderRadius:8, flexShrink:0, background:'#F0F0F5', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {imgSrc
                        ? <img src={imgUrl(imgSrc)} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={(e) => { e.target.style.display='none' }} />
                        : <Package size={16} style={{ color:'#C4C4C4' }} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate" style={{ fontFamily:'Cairo, sans-serif' }}>{name}</div>
                      <div className="text-xs text-ghost truncate" style={{ fontFamily:'Tajawal, sans-serif' }}>
                        {vendor ? `${vendor} · ` : ''}{formatNumber(orders)} طلب
                      </div>
                    </div>
                    <div className="text-sm font-black flex-shrink-0" style={{ color:'#1B6D24', fontFamily:'Cairo, sans-serif' }}>
                      {formatCurrency(rev)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Section>

        <Section title="أفضل البائعين إيراداً">
          {vendors.length === 0 ? (
            <div className="text-center py-8 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>لا توجد بيانات</div>
          ) : (
            <div className="space-y-3">
              {vendors.slice(0, 8).map((item, idx) => {
                const vnd    = item.vendor || item
                const name   = vnd.storeName?.ar || vnd.storeName?.en || '—'
                const orders = item.totalOrders ?? 0
                const rev    = item.totalRevenue ?? 0
                return (
                  <div key={vnd.id || idx} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                      style={{ background: idx < 3 ? '#000666' : '#F3F3F3', color: idx < 3 ? '#fff' : '#767683' }}>
                      {idx + 1}
                    </span>
                    <div style={{ width:36, height:36, borderRadius:8, flexShrink:0, background:'#000666', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {vnd.logo
                        ? <img src={imgUrl(vnd.logo)} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={(e) => { e.target.style.display='none' }} />
                        : <span style={{ color:'#fff', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13 }}>{name.charAt(0)}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate" style={{ fontFamily:'Cairo, sans-serif' }}>{name}</div>
                      <div className="text-xs text-ghost" style={{ fontFamily:'Tajawal, sans-serif' }}>{formatNumber(orders)} طلب</div>
                    </div>
                    <div className="text-sm font-black flex-shrink-0" style={{ color:'#FF6D00', fontFamily:'Cairo, sans-serif' }}>
                      {formatCurrency(rev)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Section>
      </div>

      {/* ── Top Searches ───────────────────────────────────────────────────── */}
      <Section title="أكثر عمليات البحث على الموقع">
        {searches.length === 0 ? (
          <div className="text-center py-8 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            لا توجد عمليات بحث مسجّلة بعد
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {searches.map((s, idx) => {
              const noResults = (s.avgResults ?? 0) === 0
              return (
                <div key={s.term || idx} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                    style={{ background: idx < 3 ? '#FF6D00' : '#F3F3F3', color: idx < 3 ? '#fff' : '#767683' }}>
                    {idx + 1}
                  </span>
                  <Search size={15} style={{ color: '#C4C4C4', flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-ink truncate" style={{ fontFamily: 'Cairo, sans-serif' }} title={s.term}>
                      {s.term}
                    </div>
                    {noResults && (
                      <div className="text-xs" style={{ color: '#BA1A1A', fontFamily: 'Tajawal, sans-serif' }}>
                        بدون نتائج — طلب غير متوفر
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-black flex-shrink-0" style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                    {formatNumber(s.count ?? 0)}
                    <span className="text-xs font-normal text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}> بحث</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>

      {/* ── Users Growth Chart ─────────────────────────────────────────────── */}
      <Section title="نمو المستخدمين الجدد">
        {growth.length === 0 ? (
          <div className="text-center py-12 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>لا توجد بيانات</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={growth} margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F3F3" />
              <XAxis dataKey="date" tick={{ fontSize:11, fontFamily:'Tajawal, sans-serif', fill:'#767683' }} />
              <YAxis tick={{ fontSize:11, fontFamily:'Tajawal, sans-serif', fill:'#767683' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="newUsers" fill="#FF6D00" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* ── Active Carts Table ─────────────────────────────────────────────── */}
      <Section title="عربات التسوق النشطة" action={
        <span style={{ fontSize:12, fontFamily:'Tajawal, sans-serif', color:'#767683' }}>
          {cartsMeta.total != null ? `${formatNumber(cartsMeta.total)} عربة` : ''}
        </span>
      }>
        {carts.length === 0 ? (
          <div className="text-center py-8 text-ghost" style={{ fontFamily:'Tajawal, sans-serif' }}>
            لا توجد عربات نشطة
          </div>
        ) : (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F9F9F9', borderBottom:'2px solid #F3F3F3' }}>
                    {['المستخدم','البريد','رقم الهاتف','عدد المنتجات','القيمة التقديرية','آخر نشاط',''].map((h) => (
                      <th key={h} style={{ textAlign:'right', padding:'10px 14px', fontSize:11, fontWeight:700, color:'#767683', whiteSpace:'nowrap', fontFamily:'Tajawal, sans-serif' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {carts.map((c, idx) => (
                    <tr key={c.cartId || idx}
                      style={{ borderBottom: idx === carts.length-1 ? 'none' : '1px solid #F3F3F3' }}
                      onMouseEnter={(e) => e.currentTarget.style.background='#F9F9F9'}
                      onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
                    >
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#1A1C1C' }}>
                          {c.user?.name || '—'}
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683', direction:'ltr', display:'block' }}>
                          {c.user?.email || '—'}
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683', direction:'ltr', display:'block' }}>
                          {c.user?.phone || '—'}
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#000666' }}>
                          {formatNumber(c.itemCount ?? 0)} منتج
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:14, color:'#FF6D00' }}>
                          {formatCurrency(c.estimatedTotal ?? 0)}
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683' }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>{new Date(c.lastActivity).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                          <span style={{ fontSize: 10, opacity: 0.7 }}>{formatRelativeTime(c.lastActivity, 'ar-EG')}</span>
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        <button
                          onClick={() => setCartModalUser(c.user)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '6px 12px', borderRadius: 8, border: 'none',
                            background: 'rgba(0,6,102,0.07)', color: '#000666',
                            fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          <Eye size={13} />
                          عرض
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cartsMeta.totalPages > 1 && (
              <div style={{ padding:'0 0 4px' }}>
                <Pagination
                  page={cartsPage}
                  totalPages={cartsMeta.totalPages}
                  total={cartsMeta.total}
                  limit={cartsLimit}
                  onPageChange={setCartsPage}
                />
              </div>
            )}
          </>
        )}
      </Section>

      {/* ── Date Range Picker Modal ─────────────────────────────────────── */}
      <DateRangePicker
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onApply={(range) => {
          setCustomRange(range)
          setRange('custom')
          setShowDatePicker(false)
        }}
        initialStart={customRange ? new Date(customRange.from) : null}
        initialEnd={customRange ? new Date(customRange.to) : null}
      />

      {cartModalUser && (
        <CartDetailModal
          user={cartModalUser}
          onClose={() => setCartModalUser(null)}
        />
      )}
    </div>
  )
}
